import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import {
  Copy,
  ShareNetwork,
  Wallet,
  ArrowsLeftRight,
  CheckCircle,
  Lightning,
} from "@phosphor-icons/react";

const EASE = [0.16, 1, 0.3, 1];
const ADDRESS = "0x5343e79A2c9E5cF6c1B3a2fBEc0aDdD8be6cce42fb";
const SHORT = "0x5343…ce42fb";

const enter = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.45, ease: EASE },
};

const ActionBtn = ({ Icon, label, highlight }) => (
  <div
    className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl py-3 text-xs font-semibold transition-colors ${
      highlight
        ? "bg-[#f5d949] text-black shadow-sm"
        : "bg-white border border-black/10 text-zinc-700"
    }`}
  >
    <Icon size={18} weight="bold" />
    {label}
  </div>
);

export default function HeroWidget({ phase = "standard" }) {
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    if (phase !== "amount") {
      setAmount(0);
      return;
    }
    const target = 1250;
    const steps = 28;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setAmount(Math.round((target / steps) * i));
      if (i >= steps) clearInterval(id);
    }, 45);
    return () => clearInterval(id);
  }, [phase]);

  return (
    <div
      data-testid="hero-widget"
      className="rounded-3xl bg-white border border-black/10 text-[#0a0a0a] p-5 shadow-xl h-[420px] flex flex-col overflow-hidden"
    >
      <AnimatePresence mode="wait">
        {/* SCREEN 1 — Standard Deposit Screen (industry norm) */}
        {phase === "standard" && (
          <motion.div key="standard" {...enter} className="flex flex-col h-full">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-zinc-500">Asset</p>
                <p className="text-sm font-semibold text-zinc-500">Network</p>
              </div>
              <div className="text-right space-y-1">
                <p className="text-sm font-bold">USDC</p>
                <p className="text-sm font-bold">Ethereum</p>
              </div>
            </div>

            <div className="mt-4 mx-auto rounded-2xl bg-white border border-black/10 p-3 shadow-sm">
              <QRCodeSVG
                value={ADDRESS}
                size={132}
                fgColor="#0a0a0a"
                bgColor="#ffffff"
                level="M"
              />
              <p className="mt-2 text-center text-[11px] font-mono text-zinc-500">
                {SHORT}
              </p>
            </div>

            <div className="mt-auto grid grid-cols-2 gap-2.5">
              <ActionBtn Icon={Copy} label="Copy" />
              <ActionBtn Icon={ShareNetwork} label="Share" />
            </div>
          </motion.div>
        )}

        {/* SCREEN 2 — Process Direct Deposit (with OpenDeposit) */}
        {phase === "direct" && (
          <motion.div key="direct" {...enter} className="flex flex-col h-full">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-zinc-500">Asset</p>
                <p className="text-sm font-semibold text-zinc-500">Network</p>
              </div>
              <div className="text-right space-y-1">
                <p className="text-sm font-bold">USDC</p>
                <p className="text-sm font-bold">Ethereum</p>
              </div>
            </div>

            <div className="mt-4 mx-auto rounded-2xl bg-white border border-black/10 p-3 shadow-sm">
              <QRCodeSVG
                value={ADDRESS}
                size={132}
                fgColor="#0a0a0a"
                bgColor="#ffffff"
                level="M"
              />
              <p className="mt-2 text-center text-[11px] font-mono text-zinc-500">
                {SHORT}
              </p>
            </div>

            <div className="mt-auto grid grid-cols-3 gap-2">
              <ActionBtn Icon={Copy} label="Copy" />
              <motion.div
                initial={{ scale: 0.92 }}
                animate={{ scale: [0.92, 1.04, 1] }}
                transition={{ duration: 0.7, ease: EASE }}
              >
                <ActionBtn Icon={Lightning} label="Direct deposit" highlight />
              </motion.div>
              <ActionBtn Icon={ShareNetwork} label="Share" />
            </div>
          </motion.div>
        )}

        {/* SCREEN 3 — Connect Wallet */}
        {phase === "connect" && (
          <motion.div
            key="connect"
            {...enter}
            className="flex flex-col h-full items-center text-center"
          >
            <p className="self-start text-[11px] uppercase tracking-[0.18em] text-zinc-400">
              Direct Deposit
            </p>
            <div className="mt-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#f5d949]">
              <Wallet size={30} weight="bold" className="text-black" />
            </div>
            <p className="mt-5 font-display text-2xl font-black">Connect wallet</p>
            <p className="mt-1 text-sm text-zinc-500">
              MetaMask · WalletConnect · Coinbase
            </p>
            <div className="mt-auto w-full rounded-full bg-[#0a0a0a] text-white font-bold py-3.5 text-sm text-center relative overflow-hidden">
              Connect Wallet
              <motion.span
                className="absolute inset-0 bg-white/15"
                initial={{ x: "-100%" }}
                animate={{ x: "100%" }}
                transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </motion.div>
        )}

        {/* SCREEN 4 — Add Amount */}
        {phase === "amount" && (
          <motion.div key="amount" {...enter} className="flex flex-col h-full">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-zinc-100">
                <Wallet size={22} weight="bold" className="text-[#0a0a0a]" />
              </div>
              <div>
                <p className="text-sm font-semibold">Deposit Wallet</p>
                <p className="text-xs text-zinc-400 font-mono">0x…a91f</p>
              </div>
            </div>
            <div className="my-3 flex items-center gap-3 text-zinc-400">
              <div className="h-px flex-1 bg-black/10" />
              <ArrowsLeftRight size={18} weight="bold" />
              <div className="h-px flex-1 bg-black/10" />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#f5d949]">
                <Wallet size={22} weight="bold" className="text-black" />
              </div>
              <div>
                <p className="text-sm font-semibold">Connected Wallet</p>
                <p className="text-xs text-zinc-400 font-mono">0x…7c30</p>
              </div>
            </div>
            <div className="mt-4 rounded-2xl bg-zinc-100 p-4">
              <p className="text-xs text-zinc-500">Amount</p>
              <p className="font-display text-3xl font-black tabular-nums">
                ${amount.toLocaleString()}
                <motion.span
                  className="inline-block w-[3px] h-7 align-middle bg-[#f5d949] ml-1"
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 0.9, repeat: Infinity }}
                />
              </p>
            </div>
            <button className="mt-auto w-full rounded-full bg-[#0a0a0a] text-white font-bold py-3.5 text-sm">
              Deposit
            </button>
          </motion.div>
        )}

        {/* SCREEN 5 — Deposit Processed */}
        {phase === "processed" && (
          <motion.div
            key="processed"
            {...enter}
            className="flex flex-col items-center justify-center text-center h-full"
          >
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: EASE, delay: 0.1 }}
              className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#f5d949]"
            >
              <CheckCircle size={32} weight="bold" className="text-black" />
            </motion.div>
            <p className="mt-5 font-display text-2xl font-black">
              Deposit processed
            </p>
            <p className="mt-1 text-sm text-zinc-500">$1,250 sent in seconds</p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <span className="text-sm font-bold text-emerald-600">Completed</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}